import axios from 'axios';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;
export const TASK_REMINDERS_CHANGED_EVENT = 'fundelite:task-reminders-changed';

function emitTaskRemindersChanged(detail = {}) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TASK_REMINDERS_CHANGED_EVENT, { detail }));
  }
}

function scopeParams(workspace, extra = {}) {
  const params = { ...extra };
  if (workspace?.scopeType === 'agency' && workspace.agencyId) {
    params.agency_id = workspace.agencyId;
  }
  return params;
}

function scopeHeaders(workspace) {
  const headers = {};
  if (workspace?.scopeType === 'agency' && workspace.agencyId) {
    headers['X-Agency-ID'] = workspace.agencyId;
  }
  return headers;
}

export async function fetchTasks(workspace, params = {}) {
  const query = scopeParams(workspace, {});
  if (params.view && params.view !== 'all') query.view = params.view;
  if (params.search) query.search = params.search;
  if (params.status) query.status = params.status;
  if (params.assigneeUserId) query.assignee_user_id = params.assigneeUserId;
  if (params.dueDateOperator) query.due_date_operator = params.dueDateOperator;
  if (params.dueDateValue != null && params.dueDateValue !== '') {
    query.due_date_value = typeof params.dueDateValue === 'string'
      ? params.dueDateValue
      : JSON.stringify(params.dueDateValue);
  }
  query.page = String(params.page || 1);
  query.page_size = String(params.pageSize || 20);
  if (params.sortField) query.sort_field = params.sortField;
  if (params.sortDirection) query.sort_direction = params.sortDirection;
  if (params.advancedFilters?.length) {
    query.advanced_filters = JSON.stringify({
      logic: 'and',
      filters: params.advancedFilters,
    });
  }
  const { data } = await axios.get(`${API}/tasks`, {
    params: query,
    headers: scopeHeaders(workspace),
  });
  return data;
}

export async function fetchTask(workspace, taskId) {
  const { data } = await axios.get(`${API}/tasks/${taskId}`, {
    params: scopeParams(workspace),
    headers: scopeHeaders(workspace),
  });
  return data;
}

export async function createTask(workspace, payload, idempotencyKey) {
  const { data } = await axios.post(`${API}/tasks`, payload, {
    params: scopeParams(workspace),
    headers: {
      ...scopeHeaders(workspace),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
  });
  emitTaskRemindersChanged({ operation: 'create', taskId: data?.taskId, task: data });
  return data;
}

export async function updateTask(workspace, taskId, payload) {
  const { data } = await axios.patch(`${API}/tasks/${taskId}`, payload, {
    params: scopeParams(workspace),
    headers: scopeHeaders(workspace),
  });
  emitTaskRemindersChanged({ operation: 'update', taskId, task: data });
  return data;
}

export async function deleteTask(workspace, taskId, deleteScope = 'this') {
  const { data } = await axios.delete(`${API}/tasks/${taskId}`, {
    params: scopeParams(workspace, { delete_scope: deleteScope }),
    headers: scopeHeaders(workspace),
  });
  emitTaskRemindersChanged({ operation: 'delete', taskId });
  return data;
}

export async function completeTask(workspace, taskId) {
  const { data } = await axios.post(
    `${API}/tasks/${taskId}/complete`,
    {},
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  emitTaskRemindersChanged({ operation: 'complete', taskId, task: data });
  return data;
}

export async function reopenTask(workspace, taskId) {
  const { data } = await axios.post(
    `${API}/tasks/${taskId}/reopen`,
    {},
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  emitTaskRemindersChanged({ operation: 'reopen', taskId, task: data });
  return data;
}

export async function duplicateTask(workspace, taskId) {
  const { data } = await axios.post(
    `${API}/tasks/${taskId}/duplicate`,
    {},
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  emitTaskRemindersChanged({ operation: 'duplicate', taskId: data?.taskId, task: data });
  return data;
}

export async function fetchAssignees(workspace, { search = '', page = 1 } = {}) {
  const { data } = await axios.get(`${API}/tasks/assignees`, {
    params: scopeParams(workspace, { search, page, page_size: 50 }),
    headers: scopeHeaders(workspace),
  });
  return data;
}

export async function fetchAssociationOptions(
  workspace,
  objectType,
  { search = '', page = 1, pageSize = 20 } = {},
) {
  const { data } = await axios.get(`${API}/tasks/association-options`, {
    params: scopeParams(workspace, {
      object_type: objectType,
      search,
      page,
      page_size: pageSize,
    }),
    headers: scopeHeaders(workspace),
  });
  return data;
}

export async function fetchTaskPreferences(workspace) {
  const { data } = await axios.get(`${API}/tasks/preferences`, {
    params: scopeParams(workspace),
    headers: scopeHeaders(workspace),
  });
  return data;
}

export async function saveTaskPreferences(workspace, tableFields) {
  const { data } = await axios.put(
    `${API}/tasks/preferences`,
    { table_fields: tableFields },
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  return data;
}

export async function saveTaskReminderDefaults(workspace, reminderDefaults) {
  const { data } = await axios.put(
    `${API}/tasks/preferences`,
    {
      reminder_defaults: {
        browser_notification: reminderDefaults?.browserNotification ?? true,
        sound: reminderDefaults?.sound ?? true,
        sound_type: reminderDefaults?.soundType || 'gentle-chime',
        sound_volume: Number(reminderDefaults?.soundVolume ?? 65),
        sound_repeat_mode: reminderDefaults?.soundRepeatMode || 'once',
        sound_duration_seconds: reminderDefaults?.soundDurationSeconds ?? null,
        offsets_minutes: reminderDefaults?.offsetsMinutes || [15],
      },
    },
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  return data;
}

export async function createBulkJob(workspace, payload) {
  const { data } = await axios.post(`${API}/tasks/bulk`, payload, {
    params: scopeParams(workspace),
    headers: scopeHeaders(workspace),
  });
  return data;
}

export async function fetchBulkJobs(workspace, params = {}) {
  const { data } = await axios.get(`${API}/tasks/bulk-actions`, {
    params: scopeParams(workspace, params),
    headers: scopeHeaders(workspace),
  });
  return data;
}

export async function pauseBulkJob(workspace, bulkJobId) {
  const { data } = await axios.post(
    `${API}/tasks/bulk-actions/${bulkJobId}/pause`,
    {},
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  return data;
}

export async function resumeBulkJob(workspace, bulkJobId) {
  const { data } = await axios.post(
    `${API}/tasks/bulk-actions/${bulkJobId}/resume`,
    {},
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  return data;
}

export async function cancelBulkJob(workspace, bulkJobId) {
  const { data } = await axios.post(
    `${API}/tasks/bulk-actions/${bulkJobId}/cancel`,
    {},
    { params: scopeParams(workspace), headers: scopeHeaders(workspace) },
  );
  return data;
}

export function buildCreatePayload(form) {
  return {
    title: (form.title || '').trim(),
    description_html: form.descriptionHtml || '',
    status: form.status || 'pending',
    priority: form.priority || 'normal',
    due_at: form.dueAt || null,
    timezone: form.timezone,
    assignee_user_id: form.assigneeUserId || null,
    associations: (form.associations || []).map((a) => ({
      object_type: a.objectType,
      object_id: a.objectId,
    })),
    reminders: {
      enabled: Boolean(form.reminders?.enabled),
      browser_notification: form.reminders?.browserNotification ?? true,
      sound: form.reminders?.sound ?? true,
      sound_type: form.reminders?.soundType || 'gentle-chime',
      sound_volume: Number(form.reminders?.soundVolume ?? 65),
      sound_repeat_mode: form.reminders?.soundRepeatMode || 'once',
      sound_duration_seconds: form.reminders?.soundDurationSeconds ?? null,
      offsets_minutes: form.reminders?.offsetsMinutes || [15],
    },
    recurrence: form.recurrenceEnabled
      ? {
          frequency: form.recurrence.frequency,
          interval: Number(form.recurrence.interval) || 1,
          weekdays: form.recurrence.weekdays || [],
          monthday: form.recurrence.monthday || null,
          start_at: form.dueAt || new Date().toISOString(),
          timezone: form.timezone,
          end_mode: form.recurrence.endMode || 'never',
          count: form.recurrence.count || null,
          until: form.recurrence.until || null,
        }
      : null,
  };
}
